// Pure helpers for purging OLD documents from the `stocktake_scanned` collection
// by date. Kept framework-free (no React / Firestore imports) so the cutoff logic
// can be unit tested — this is destructive, so the "what gets deleted" decision is
// isolated here and covered by stocktakePurgeHelper.test.jsx.

function isValidDate(d) {
  return d instanceof Date && !Number.isNaN(d.getTime());
}

// Convert a `stocktake_scanned` doc's ScannedTime into a JS Date, or null when it is
// missing / unparseable. Handles Firestore Timestamp (.toDate()), the raw
// { seconds } shape, an ISO string, or an actual Date.
export function parseScannedTime(data) {
  const ts = data?.ScannedTime;
  if (!ts) return null;

  if (typeof ts.toDate === "function") {
    try {
      const d = ts.toDate();
      return isValidDate(d) ? d : null;
    } catch {
      return null;
    }
  }
  if (typeof ts.seconds === "number") {
    const d = new Date(ts.seconds * 1000);
    return isValidDate(d) ? d : null;
  }
  if (ts instanceof Date) return isValidDate(ts) ? ts : null;
  if (typeof ts === "string") {
    const ms = Date.parse(ts);
    return Number.isNaN(ms) ? null : new Date(ms);
  }
  return null;
}

// Local (not UTC) YYYY-MM-DD key for a Date, or null if invalid.
export function localDateKey(date) {
  if (!isValidDate(date)) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// Local midnight (start of day) for a "YYYY-MM-DD" key, or null if malformed.
export function startOfLocalDay(dateKey) {
  if (typeof dateKey !== "string") return null;
  const parts = dateKey.split("-").map(Number);
  const [y, m, d] = parts;
  if (parts.length !== 3 || !y || !m || !d) return null;
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}

// Today's local date key — the default cutoff ("keep today's scans").
export function todayKey(now = new Date()) {
  return localDateKey(now);
}

/**
 * Decide which scans are "old" relative to a cutoff day.
 *
 * @param {Array<{id:string, time: Date|null}>} scans
 * @param {object} opts
 * @param {string} opts.cutoffKey   "YYYY-MM-DD". Scans strictly BEFORE local midnight
 *                                  of this day are old. Scans on/after are kept.
 * @param {boolean} [opts.deleteUndated=false]  Include scans with no parseable time in
 *                                  the delete set. Default false → undated scans are kept.
 * @returns {{
 *   toDelete: string[],
 *   deleteCount: number,
 *   keepCount: number,
 *   undatedCount: number,
 *   byDate: Array<{dateKey:string, count:number, willDelete:boolean}>
 * }}
 */
export function partitionScans(scans, { cutoffKey, deleteUndated = false } = {}) {
  const cutoff = startOfLocalDay(cutoffKey);
  const cutoffMs = cutoff ? cutoff.getTime() : null;

  const toDelete = [];
  let undatedCount = 0;
  const dateBuckets = new Map(); // dateKey -> count

  for (const s of scans || []) {
    const time = isValidDate(s?.time) ? s.time : null;

    if (!time) {
      undatedCount += 1;
      if (deleteUndated) toDelete.push(s.id);
      continue;
    }

    const key = localDateKey(time);
    dateBuckets.set(key, (dateBuckets.get(key) || 0) + 1);

    // Delete only when strictly before the cutoff day. A null/invalid cutoff deletes
    // nothing dated (fail safe).
    if (cutoffMs != null && time.getTime() < cutoffMs) {
      toDelete.push(s.id);
    }
  }

  const byDate = Array.from(dateBuckets.entries())
    .map(([dateKey, count]) => ({
      dateKey,
      count,
      willDelete: cutoffMs != null && startOfLocalDay(dateKey).getTime() < cutoffMs,
    }))
    .sort((a, b) => (a.dateKey < b.dateKey ? -1 : a.dateKey > b.dateKey ? 1 : 0));

  return {
    toDelete,
    deleteCount: toDelete.length,
    keepCount: (scans?.length || 0) - toDelete.length,
    undatedCount,
    byDate,
  };
}
